import React, { useEffect } from 'react';
import { Route, Routes as RouterRoutes, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import initAnalytics, { trackEventRudder } from 'rubriq/helpers/analytics';
import withRouter from 'shared/helper/with-router';
import CatchableRoute from 'researcher/components/CatchableRoute';
import GenericError from 'researcher/components/GenericError';
import GlobalLoading from 'researcher/components/GlobalLoading';
import HardRedirect from 'researcher/components/HardRedirect';
import HomeRedirect from 'researcher/components/HomeRedirect';
import ModalFlashMessage from 'shared/components/ModalFlashMessage';
import Redirect from 'shared/components/Redirect';
import { ImpersonatorNotification } from 'shared/account';
import featureEnabled from 'shared/helper/feature';
import { getChinaLocalization } from 'shared/account/reducer';
import { getApiBaseUrl } from 'shared/helper/request';
import { initMixpanel } from 'rubriq/helpers/mixpanel';
import hasStatisticsConsent from 'rubriq/helpers/cookieConsent';
import RubriqRoutes from '../rubriq/Routes';
import CheckAuth from './components/CheckAuth';
import AccountRoutes from './account/Routes';
import DashboardRoutes from './dashboard/Routes';
import DigitalEditingRoutes from './digital-editing/Routes';
import DigitalTranslationsRoutes from './digital-translations/Routes';
import InvoiceRoutes from './invoice/Routes';
import { AuthenticationRoutes } from './authentication';
import { CommonLayout } from './application';
// import { OrderFormRoutes } from './order-form';
// import HomeRoutes from './home/Routes';
import ChineseDigitalTranslationTrialContainer from './digital-translations/components/ChineseDigitalTranslationTrialContainer';
var Routes = function Routes(_ref) {
  var accountInformationMissing = _ref.accountInformationMissing,
    applicationReady = _ref.applicationReady,
    creditNotesLocked = _ref.creditNotesLocked,
    localizationMismatch = _ref.localizationMismatch,
    location = _ref.location,
    pathname = _ref.pathname,
    requiresLocalizationRedirect = _ref.requiresLocalizationRedirect,
    requiresWfoeMigrationStatusPage = _ref.requiresWfoeMigrationStatusPage,
    siteLanguage = _ref.siteLanguage,
    urlIsMissingLanguage = _ref.urlIsMissingLanguage,
    mixpanel = _ref.mixpanel;
  var isChinaUser = useSelector(getChinaLocalization);
  var rubriqMixpanelEnabled = featureEnabled('rubriq_mixpanel');
  var rubriqRudderEnabled = featureEnabled('rubriq_rudderstack');
  useEffect(function () {
    var tryInitAnalytics = function tryInitAnalytics() {
      if (rubriqRudderEnabled) {
        initAnalytics('39Q5aSEScArkktyABbZD8oWdLhA', 'https://mpslimitedtyuf.dataplane.rudderstack.com');
      }
      var hasConsent = hasStatisticsConsent();
      if (!isChinaUser && hasConsent && rubriqMixpanelEnabled && mixpanel) {
        initMixpanel(mixpanel);
      }
    };
    tryInitAnalytics();
    window.addEventListener('CookiebotOnAccept', tryInitAnalytics);
    window.addEventListener('CookiebotOnLoad', tryInitAnalytics);
    return function () {
      window.removeEventListener('CookiebotOnAccept', tryInitAnalytics);
      window.removeEventListener('CookiebotOnLoad', tryInitAnalytics);
    };
  }, [isChinaUser, mixpanel, rubriqRudderEnabled, rubriqMixpanelEnabled]);
  var curieFeatureEnabled = featureEnabled('curie_is_launched');
  var chinaLocalization = useSelector(getChinaLocalization);
  var enableChinaCartPurchase = chinaLocalization && !featureEnabled('rubriq_china_cart_purchase');
  var _useSelector = useSelector(function (state) {
      return state.application.config;
    }),
    chatBot = _useSelector.chatBot;
  useEffect(function () {
    window.scrollTo(0, 0);
  }, [pathname]);
  var navigate = useNavigate();
  useEffect(function () {
    var restrictLanguages = featureEnabled('rubriq_restrict_languages');
    if (!restrictLanguages) return;
    var match = location.pathname.match(/^\/(jp|br|es)/);
    if (match) {
      var newPath = location.pathname.replace(/^\/(jp|br|es)/, '/en');
      navigate("".concat(newPath).concat(location.search), {
        replace: true
      });
    }
  }, [location.pathname]);
  if (!applicationReady || requiresLocalizationRedirect) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GlobalLoading, {
      className: "position-relative"
    }), requiresLocalizationRedirect ? /*#__PURE__*/React.createElement(HardRedirect, {
      to: "/api/account/redirect".concat(location.search)
    }) : null);
  }
  var ExternalNavigate = function ExternalNavigate(_ref2) {
    var to = _ref2.to;
    var navigate = useNavigate();

    // Handle external URLs
    if (to.startsWith('http')) {
      window.location.href = to;
      return null;
    }

    // Use React Router for internal navigation
    navigate(to, {
      replace: true
    });
    return null;
  };
  var MeiqiaScript = function MeiqiaScript(_ref3) {
    var meiqiaEnterpriseId = _ref3.meiqiaEnterpriseId;
    useEffect(function () {
      var script = document.createElement('script');
      script.async = true;
      script.charset = 'UTF-8';
      script.src = 'https://static.meiqia.com/dist/meiqia.js';
      document.body.appendChild(script);
      script.onload = function () {
        // eslint-disable-next-line no-underscore-dangle
        window._MEIQIA =
        // eslint-disable-next-line no-underscore-dangle
        window._MEIQIA || /* eslint-disable-next-line func-names */
        function () {
          /* eslint-disable prefer-rest-params */
          // eslint-disable-next-line no-underscore-dangle
          (window._MEIQIA.a = window._MEIQIA.a || []).push(arguments);
        };
        // eslint-disable-next-line no-underscore-dangle
        window._MEIQIA('entId', meiqiaEnterpriseId);
      };
      return function () {
        document.body.removeChild(script);
      };
    }, [meiqiaEnterpriseId]);
    return null;
  };
  if (creditNotesLocked || localizationMismatch) {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ModalFlashMessage, {
      message: {
        message: localizationMismatch ? 'localization-mismatch-message' : 'credit-notes-lockout-message',
        type: 'warning'
      },
      dismissMessage: null
    }));
  }
  if (accountInformationMissing) {
    return /*#__PURE__*/React.createElement(HardRedirect, {
      to: "/account"
    });
  }

  // If the URL doesn't contain language, lets fix that
  if (urlIsMissingLanguage) {
    return /*#__PURE__*/React.createElement(Navigate, {
      to: "/".concat(siteLanguage).concat(location.pathname).concat(location.search),
      replace: true
    });
  }
  var restrictLanguages = featureEnabled('rubriq_restrict_languages');
  var currentLangMatch = location.pathname.match(/^\/(cn|jp|en|br|es)/);
  var currentLang = currentLangMatch ? currentLangMatch[1] : null;
  var isChinaDomain = window.location.hostname.includes('-china');
  if (restrictLanguages && currentLang) {
    var targetLang = currentLang;
    if (isChinaDomain) {
      if (currentLang !== 'cn') {
        targetLang = 'cn';
      }
    } else if (['jp', 'br', 'es'].includes(currentLang)) {
      targetLang = 'en';
    }
    if (targetLang !== currentLang) {
      var newPath = location.pathname.replace(/^\/(cn|jp|en|br|es)/, "/".concat(targetLang));
      return /*#__PURE__*/React.createElement(Navigate, {
        to: "".concat(newPath).concat(location.search),
        replace: true
      });
    }
  }

  // Handle ROW -> WFOE China migration users
  if (requiresWfoeMigrationStatusPage) {
    if (!location.pathname.endsWith('/researcher/wfoe-migration-status') && !location.pathname.includes('login') && !location.pathname.includes('register')) {
      return /*#__PURE__*/React.createElement(Redirect, {
        to: "/researcher/wfoe-migration-status"
      });
    }
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CommonLayout, null), /*#__PURE__*/React.createElement(RouterRoutes, null, /*#__PURE__*/React.createElement(Route, {
    path: "/:lang/login/*",
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(AuthenticationRoutes, null))
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/:lang/register/*",
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(AuthenticationRoutes, null))
  }), /*#__PURE__*/React.createElement(Route, {
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(CheckAuth, null, /*#__PURE__*/React.createElement(AccountRoutes, null))),
    path: "/:lang/account/*"
  }), /*#__PURE__*/React.createElement(Route, {
    auth: true,
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(CheckAuth, null, /*#__PURE__*/React.createElement(ChineseDigitalTranslationTrialContainer, null))),
    path: "/:lang/chinese-digital-trial"
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/:lang/researcher/*",
    element: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Outlet, null))
  }, /*#__PURE__*/React.createElement(Route, {
    path: "*",
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(CheckAuth, null, /*#__PURE__*/React.createElement(DashboardRoutes, null)))
  }), /*#__PURE__*/React.createElement(Route, {
    path: "index/*",
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(CheckAuth, null, /*#__PURE__*/React.createElement(DashboardRoutes, null)))
  }), /*#__PURE__*/React.createElement(Route, {
    path: "digital-editing/*",
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(CheckAuth, null, /*#__PURE__*/React.createElement(DigitalEditingRoutes, null)))
  }), /*#__PURE__*/React.createElement(Route, {
    path: "digital-translations/*",
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(CheckAuth, null, /*#__PURE__*/React.createElement(DigitalTranslationsRoutes, null)))
  }), /*#__PURE__*/React.createElement(Route, {
    path: "invoices/*",
    element: /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(CheckAuth, null, /*#__PURE__*/React.createElement(InvoiceRoutes, null)))
  })), curieFeatureEnabled && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Route, {
    path: "/:lang/curie/*",
    element: /*#__PURE__*/React.createElement(Navigate, {
      to: "".concat(location.pathname.replace('/curie', '/rubriq')).concat(location.search),
      replace: true
    })
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/:lang/rubriq/*",
    element: enableChinaCartPurchase ? /*#__PURE__*/React.createElement(ExternalNavigate, {
      to: "".concat(getApiBaseUrl()).concat(window.location.pathname).concat(window.location.search)
    }) : /*#__PURE__*/React.createElement(CatchableRoute, null, /*#__PURE__*/React.createElement(CheckAuth, null, /*#__PURE__*/React.createElement(RubriqRoutes, null)))
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/:lang/c/curiefreetrial/*",
    element: /*#__PURE__*/React.createElement(ExternalNavigate, {
      to: "".concat(getApiBaseUrl()).concat(window.location.pathname.replace(/curiefreetrial/gi, 'CurieFreeTrial')).concat(window.location.search)
    })
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/:lang/c/curiefreetrialchina/*",
    element: /*#__PURE__*/React.createElement(ExternalNavigate, {
      to: "".concat(getApiBaseUrl()).concat(window.location.pathname.replace(/curiefreetrialchina/gi, 'CurieFreeTrialChina')).concat(window.location.search)
    })
  })), /*#__PURE__*/React.createElement(Route, {
    element: /*#__PURE__*/React.createElement(GenericError, null),
    path: "/:lang/error"
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/:lang?",
    element: /*#__PURE__*/React.createElement(HomeRedirect, null)
  })), chinaLocalization && /*#__PURE__*/React.createElement(MeiqiaScript, {
    meiqiaEnterpriseId: chatBot.meiqiaEnterpriseId
  }), /*#__PURE__*/React.createElement(ImpersonatorNotification, null));
};
var mapStateToProps = function mapStateToProps(_ref4, _ref5) {
  var application = _ref4.application,
    user = _ref4.user;
  var location = _ref5.location;
  return {
    accountInformationMissing: user.loggedIn && user.missingFields.length > 0 && (location === null || location === void 0 ? void 0 : location.pathname.indexOf('/account')) === -1,
    applicationReady: application.status.ready && application.status.translationsLoaded && user.status.loaded,
    creditNotesLocked: user.creditNotesLocked,
    location: location,
    localizationMismatch: user.localizationMismatch,
    requiresLocalizationRedirect: user.requiresLocalizationRedirect,
    requiresWfoeMigrationStatusPage: user.wfoeMigrationStatus.inMigratingGroup && !user.impersonatorIdentity && !(user.wfoeMigrationStatus.acceptedTermsOfService && user.wfoeMigrationStatus.migrated),
    siteLanguage: application.siteLanguage,
    urlIsMissingLanguage: !location.pathname.match(/\/(cn|jp|en|br|es)\/?/),
    pathname: location.pathname,
    mixpanel: application.mixpanel.apiToken
  };
};
export default withRouter(connect(mapStateToProps)(Routes));